import React, { useMemo } from 'react';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { UserCommentItemSkeleton } from '@components/main-components/user/UserCommentItem';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  ActivityFeedHookDocument,
  DeletePostCommentDocument,
  PostCommentDocument,
  PostCommentsDocument,
  useQuery,
} from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';
import { PostCommentsInput } from './Input';
import { PostCommentsRow } from './Row';

const MAX_NUMBER_OF_COMMENTS_IN_PREVIEW = 3;

RefetchOnComplete({
  refetch: [PostCommentsDocument, ActivityFeedHookDocument],
  trigger: [PostCommentDocument, DeletePostCommentDocument],
});

export function PostRepliesPreview({
  postId,
  postCommentCount,
  postWebappUri,
}: {
  postId: string;
  postCommentCount: number;
  postWebappUri: string;
}) {
  const { data, isLoading } = useQuery(PostCommentsDocument, {
    staleTime: 0,
    variables: {
      postId,
      pagination: {
        first: MAX_NUMBER_OF_COMMENTS_IN_PREVIEW,
        sort: {
          createdAt: 'DESC',
        },
      },
    },
  });

  const postComments = useMemo(
    () => data?.data.post?.comments.edges ?? [],
    [data?.data.post?.comments.edges],
  );

  const replies = useMemo(() => {
    return postComments.map(item => (
      <PostCommentsRow key={item.node.id} postCommentsRowFrag={item.node} />
    ));
  }, [postComments]);

  const repliesLeft = useMemo(() => {
    return Math.max(postCommentCount - postComments.length, 0);
  }, [postCommentCount, postComments.length]);

  const numberOfLoadingItems = Math.min(MAX_NUMBER_OF_COMMENTS_IN_PREVIEW, postCommentCount);

  return (
    <div className="w-full pt-7">
      <PostCommentsInput postId={postId} type="feed_replies_preview" />

      {/* Why 66px? Check services/webapp/components/Feed/Activities/FeedActivityItem.tsx */}
      <div className="w-full pt-4 lg:pl-[66px]">
        {isLoading ? (
          <div className="flex flex-col gap-4 pt-[10px]">
            {Array.from({ length: numberOfLoadingItems }).map((_, index) => (
              <UserCommentItemSkeleton key={index} />
            ))}
          </div>
        ) : (
          <>
            {replies}

            {!!repliesLeft && (
              <AccessibleLink
                pathnameUri={webappUriPath(postWebappUri)}
                className="font-base text-base-s font-semibold text-base900"
              >
                See {repliesLeft} {repliesLeft === 1 ? 'reply' : 'replies'}
              </AccessibleLink>
            )}
          </>
        )}
      </div>
    </div>
  );
}
